import { ref, computed } from 'vue'

// WebSocket Configuration
const WS_BASE_URL = 'wss://ai-gateway.manonsanoi.workers.dev/ws'

// WebSocket state management
const wsState = ref({
  connection: null,
  isConnected: false,
  isConnecting: false,
  connectionId: null,
  lastPing: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectInterval: 3000, // 3 seconds
  notifications: []
})

// Event handlers storage
const eventHandlers = ref({
  new_message: [],
  new_session: [],
  status_change: [],
  connection_open: [],
  connection_close: [],
  connection_error: []
})

export const useWebSocket = (hostname = 'localhost') => {
  // Generate WebSocket URL with hostname
  const getWebSocketUrl = () => {
    return `${WS_BASE_URL}?hostname=${encodeURIComponent(hostname)}`
  }

  // Add event listener for specific notification type
  const addEventListener = (type, handler) => {
    if (eventHandlers.value[type]) {
      eventHandlers.value[type].push(handler)
    } else {
      console.warn(`Unknown event type: ${type}`)
    }
  }

  // Remove event listener
  const removeEventListener = (type, handler) => {
    if (eventHandlers.value[type]) {
      const index = eventHandlers.value[type].indexOf(handler)
      if (index > -1) {
        eventHandlers.value[type].splice(index, 1)
      }
    }
  }

  // Trigger event handlers
  const triggerEvent = (type, data) => {
    if (eventHandlers.value[type]) {
      eventHandlers.value[type].forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          console.error(`Error in ${type} event handler:`, error)
        }
      })
    }
  }

  // Handle incoming WebSocket messages
  const handleMessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      console.log('[WebSocket] Received trigger event:', data)

      // Handle different notification types - only trigger reload actions
      switch(data.type) {
        case 'new_message':
          console.log('💬 New message trigger - calling reload')
          triggerEvent('new_message', data)
          break
        case 'new_session':
          console.log('🆕 New session trigger - calling reload')
          triggerEvent('new_session', data)
          break
        case 'status_change':
          console.log('📊 Status change trigger - SKIPPED (disabled for testing)')
          // triggerEvent('status_change', data) // Disabled for testing
          break
        case 'ping':
          // Handle ping/pong for connection health
          wsState.value.lastPing = new Date()
          if (wsState.value.connection && wsState.value.connection.readyState === WebSocket.OPEN) {
            wsState.value.connection.send(JSON.stringify({ type: 'pong' }))
          }
          break
        default:
          console.log('🔔 Unknown trigger event:', data.type, data)
      }
    } catch (error) {
      console.error('[WebSocket] Error parsing message:', error, event.data)
    }
  }

  // Connect to WebSocket
  const connect = () => {
    if (wsState.value.isConnecting || wsState.value.isConnected) {
      console.log('[WebSocket] Already connected or connecting')
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        wsState.value.isConnecting = true
        const wsUrl = getWebSocketUrl()
        
        console.log('[WebSocket] Connecting to:', wsUrl)
        wsState.value.connection = new WebSocket(wsUrl)

        wsState.value.connection.onopen = (event) => {
          console.log('[WebSocket] Connected successfully')
          wsState.value.isConnected = true
          wsState.value.isConnecting = false
          wsState.value.reconnectAttempts = 0
          wsState.value.connectionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          
          triggerEvent('connection_open', { event, connectionId: wsState.value.connectionId })
          resolve()
        }

        wsState.value.connection.onmessage = handleMessage

        wsState.value.connection.onclose = (event) => {
          console.log('[WebSocket] Connection closed:', event.code, event.reason)
          wsState.value.isConnected = false
          wsState.value.isConnecting = false
          wsState.value.connection = null
          
          triggerEvent('connection_close', { event, wasClean: event.wasClean })
          
          // Auto-reconnect if not a clean close and within retry limits
          if (!event.wasClean && wsState.value.reconnectAttempts < wsState.value.maxReconnectAttempts) {
            setTimeout(() => {
              wsState.value.reconnectAttempts++
              console.log(`[WebSocket] Reconnecting... (attempt ${wsState.value.reconnectAttempts}/${wsState.value.maxReconnectAttempts})`)
              connect()
            }, wsState.value.reconnectInterval)
          }
        }

        wsState.value.connection.onerror = (error) => {
          console.error('[WebSocket] Connection error:', error)
          wsState.value.isConnecting = false
          
          triggerEvent('connection_error', { error })
          reject(error)
        }

      } catch (error) {
        console.error('[WebSocket] Failed to create connection:', error)
        wsState.value.isConnecting = false
        triggerEvent('connection_error', { error })
        reject(error)
      }
    })
  }

  // Disconnect from WebSocket
  const disconnect = () => {
    if (wsState.value.connection) {
      console.log('[WebSocket] Disconnecting...')
      wsState.value.connection.close(1000, 'Manual disconnect')
      wsState.value.connection = null
      wsState.value.isConnected = false
      wsState.value.isConnecting = false
      wsState.value.connectionId = null
    }
  }

  // Get recent notifications (kept for compatibility)
  const getRecentNotifications = (limit = 10) => {
    return wsState.value.notifications
      .slice(-limit)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  }

  // Clear notifications
  const clearNotifications = () => {
    wsState.value.notifications = []
  }

  // Get connection statistics
  const getConnectionStats = () => {
    return {
      isConnected: wsState.value.isConnected,
      isConnecting: wsState.value.isConnecting,
      connectionId: wsState.value.connectionId,
      reconnectAttempts: wsState.value.reconnectAttempts,
      lastPing: wsState.value.lastPing,
      notificationCount: wsState.value.notifications.length,
      hostname: hostname
    }
  }

  return {
    // State
    isConnected: computed(() => wsState.value.isConnected),
    isConnecting: computed(() => wsState.value.isConnecting),
    connectionId: computed(() => wsState.value.connectionId),
    notifications: computed(() => wsState.value.notifications),
    
    // Methods
    connect,
    disconnect,
    addEventListener,
    removeEventListener,
    getRecentNotifications,
    clearNotifications,
    getConnectionStats
  }
}

// Helper function to format notification for display
export const formatNotification = (notification) => {
  const { type, data, timestamp, session_id } = notification
  
  switch (type) {
    case 'new_message':
      return {
        title: '💬 ข้อความใหม่',
        message: `มีข้อความใหม่ในเซสชัน ${session_id}`,
        timestamp: timestamp,
        type: 'info'
      }
    case 'new_session':
      return {
        title: '🆕 เซสชันใหม่',
        message: `เซสชันใหม่ถูกสร้างขึ้น: ${session_id}`,
        timestamp: timestamp,
        type: 'success'
      }
    case 'status_change':
      return {
        title: '📊 สถานะเปลี่ยนแปลง',
        message: `สถานะเปลี่ยนเป็น: ${data?.status || 'Unknown'}`,
        timestamp: timestamp,
        type: 'warning'
      }
    default:
      return {
        title: '🔔 การแจ้งเตือน',
        message: `ประเภท: ${type}`,
        timestamp: timestamp,
        type: 'default'
      }
  }
}
